<template>
  <div class="chart-panel__chart">

    <div v-show="!isReady" class="mt-5">
      <ui-spinner/>
    </div>

    <LineChart 
      v-show="isReady"
      ref="chart"
      :data="chartData" 
      :options="chartOptions" 
    />

  </div> <!-- .chart-panel__chart -->
</template>

<script>
import { nextTick } from 'vue'
import { cloneDeep, throttle } from 'lodash'
import moment from 'moment'
import UiSpinner from '../../../../common/components/UiSpinner'
import { formatCurrency, formatNumber } from '../../../../common/helpers'
import LockedDatePlugin from '../../../../common/chartjs/LockedDatePlugin'
import AnnotationPlugin from 'chartjs-plugin-annotation'
import {
  Chart as ChartJS,
  CategoryScale,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Title
} from 'chart.js'
import { Line as LineChart } from 'vue-chartjs'

ChartJS.register(
  AnnotationPlugin,
  CategoryScale,
  Filler,
  LinearScale,
  LineElement,
  LockedDatePlugin,
  PointElement,
  Title
)

export default {
  props: ['pricingData', 'fullPricingData', 'pricingComponent', 'term', 'termUsage', 'pricingDataTotals', 'displayTotal', 'chartLockedDate'],
  components: {
    LineChart,
    UiSpinner,
  },
  data() {
    return {
      isReady: false,
    }
  },
  mounted() {
    nextTick(() => {

      // Add an event listener for when a date is locked.
      // The custom `lockedDate` event is emitted from the `LockedDatePlugin` import.
      this.$refs.chart.chart.canvas.addEventListener('lockedDate', (e) => {
        this.setLockedDate(e.detail.date)
      })

      this.isReady = true
    })
  },
  watch: {
    /**
     * Update the chart when the parent sets or clears the locked date.
     */
    chartLockedDate(newValue) {
      if (newValue) {
        this.$refs.chart.chart.update()
      } else {
        this.clearLockedDate()
      }
    },
  },
  computed: {
    /**
     * The chart's data.
     * @see https://www.chartjs.org/docs/latest/charts/line.html#dataset-properties
     */
    chartData() {
      return {
        labels: this.dates,
        datasets: [
          {
            label: 'High',
            data: this.highVals,
            fill: '+2',
            order: 3,
            backgroundColor: 'rgba(80, 105, 169, .2)',
            borderColor: '#9f9f9f',
            pointHoverBackgroundColor: '#9f9f9f',
            pointHoverBorderColor: '#ffffff',
            pointHoverBorderWidth: 3,
            pointHoverRadius: 6,
          },
          {
            label: 'Best',
            data: this.avgVals,
            fill: '+1',
            order: 2,
            backgroundColor: 'rgba(80, 105, 169, .2)',
            borderColor: '#294289',
            pointHoverBackgroundColor: '#294289',
            pointHoverBorderColor: '#ffffff',
            pointHoverBorderWidth: 3,
            pointHoverRadius: 6,
          },
          {
            label: 'Low',
            data: this.lowVals,
            order: 1,
            borderColor: '#9f9f9f',
            pointHoverBackgroundColor: '#9f9f9f',
            pointHoverBorderColor: '#ffffff',
            pointHoverBorderWidth: 3,
            pointHoverRadius: 6,
          }
        ]
      }
    },
    /**
     * The chart's options.
     */
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        events: ['click', 'mousemove'],
        /**
         * Set the current chart data, which is used to change the 
         * price values displayed above the chart.
         */
        onHover: throttle((event, activeElements) => {
          if (activeElements.length > 0) {
            const index = activeElements[0].index
            const date = this.dates[ index ]

            if (! this.chartLockedDate) {
              this.setCurrentChartPricingDate(date)
            }
          }
        }, 500),
        borderColor: '#9f9f9f',
        layout: {
          padding: {
            top: 20,
            right: 65, // Allow space for the "Click to Lock Date" tooltip.
            bottom: 10,
            left: 10,
          },
        },
        elements: {
          point: {
            borderWidth: 0,
            radius: 0,
          },
          line: {
            borderCapStyle: 'round',
            tension: .2,
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
            ticks: {
              maxTicksLimit: 20,
              minRotation: 45,
              color: '#393838',
              font: {
                family: '"Open Sans", sans-serif',
                size: 10.5,
              },
            },
          },
          y: {
            title: {
              display: true,
              text: '$/kWh',
              color: '#393838',
              font: {
                family: '"Open Sans", sans-serif',
                size: 13,
                weight: 500,
              },
            },
            min: this.lowestValue - .001,
            max: this.highestValue + .001,
            ticks: {
              color: '#393838',
              font: {
                family: '"Open Sans", sans-serif',
                size: 11,
                weight: 500,
              },
              callback(value, index, ticks) {
                return formatCurrency(value, 4)
              }
            },
          }
        },
        plugins: {
          annotation: {
            annotations: this.getChartAnnotations()
          },
          lockedDate: {
            getLockedDate: () => this.chartLockedDate,
            displayPrice: false,
          },
        },
      }
    },
    /**
     * Determine if we're viewing the total of the pricing components.
     */
    isTotal() {
      return this.displayTotal && Object.keys(this.pricingDataTotals).length
    },
    /**
     * Collect the data dates.
     */
    dates() {
      let datesArray = []

      if (this.isTotal) {
        for (let [key, value] of Object.entries(this.pricingDataTotals)) {
          datesArray.push(moment(key).format('M/D/YY'))
        }

      } else if (this.pricingData) {
        datesArray = this.pricingData.map(item => moment(item.date).format('M/D/YY'))
      }

      return datesArray.reverse()
    },
    /**
     * Collect the "average" data values.
     */
    avgVals() {
      let valArray = []

      if (this.isTotal) {
        for (let [key, value] of Object.entries(this.pricingDataTotals)) {
          valArray.push(formatNumber((this.pricingDataTotals[key].avg / this.termUsage) + (this.pricingDataTotals[key].broker_fee * 1), 4))
        }

      } else if (this.pricingData) {
        valArray = this.pricingData.map(item => formatNumber((item.avg / item.term_usage) + (item.broker_fee * 1), 4))
      }

      return valArray.reverse()
    },
    /**
     * Collect the "low" data values.
     */
    lowVals() {
      let valArray = []

      if (this.isTotal) {
        for (let [key, value] of Object.entries(this.pricingDataTotals)) {
          valArray.push(formatNumber((this.pricingDataTotals[key].low / this.termUsage) + (this.pricingDataTotals[key].broker_fee * 1), 4))
        }

      } else if (this.pricingData) {
        valArray = this.pricingData.map(item => formatNumber((item.low / item.term_usage) + (item.broker_fee * 1), 4))
      }

      return valArray.reverse()
    },
    /**
     * Collect the "high" data values.
     */
    highVals() {
      let valArray = []

      if (this.isTotal) {
        for (let [key, value] of Object.entries(this.pricingDataTotals)) {
          valArray.push(formatNumber((this.pricingDataTotals[key].high / this.termUsage) + (this.pricingDataTotals[key].broker_fee * 1), 4))
        }

      } else if (this.pricingData) {
        valArray = this.pricingData.map(item => formatNumber((item.high / item.term_usage) + (item.broker_fee * 1), 4))
      }

      return valArray.reverse()
    },
    /**
     * Get the highest data value.
     */
    highestValue() {
      if (this.highVals) {
        return Math.max(...this.highVals)
      }
    },
    /**
     * Get the lowest data value.
     */
    lowestValue() {
      if (this.lowVals) {
        return Math.min(...this.lowVals)
      }
    },
  },
  methods: {
    /**
     * Set the current chart data, which is used to change the 
     * price values displayed above the chart.
     */
    setCurrentChartPricingDate(date) {
      const pricingData = cloneDeep(this.fullPricingData)
      let chartComponentData = [],
          componentData

      for (let [key, value] of Object.entries(pricingData)) {
        componentData = pricingData[key].filter(item => moment(item.date).format('M/D/YY') === moment(date, 'M/D/YY').format('M/D/YY'))

        chartComponentData.push(componentData[0])
      }

      this.$store.dispatch('setCurrentPricingChartData', chartComponentData)
    },
    /**
     * Set the locked date.
     */
    setLockedDate(date) {
      this.$emit('lockDate', date)
      this.setCurrentChartPricingDate(date)
    },
    /**
     * Clear the locked date.
     */
    clearLockedDate() {
      this.$emit('setLockedChartDate', undefined)
      this.$refs.chart.chart.update()
    },
    /**
     * Collect the annotations for the chart.
     */
    getChartAnnotations() {
      let pricingAlert,
          labelContent,
          pricingAlertAnnotations = {}

      const currentDataRequest = this.$store.getters.currentDataRequest
      if (currentDataRequest && currentDataRequest.pricing_alerts) {

        // Price Alerts
        for (let i = 0; i < currentDataRequest.pricing_alerts.length; i++) {
          pricingAlert = currentDataRequest.pricing_alerts[i]

          // Only annotate for the selected/current pricing component and term.
          if (this.pricingComponent) {
            if (
              pricingAlert.pricing_component_id != this.pricingComponent.id ||
              pricingAlert.term != this.term
            ) {
              continue
            }

          // If no pricing component, it's an alert for the "Total".
          } else {
            if (
              pricingAlert.pricing_component_id ||
              pricingAlert.term != this.term
            ) {
              continue
            }
          }

          // Line
          pricingAlertAnnotations[ pricingAlert.trigger + 'Line' + i ] = {
            type: 'line',
            yMin: pricingAlert.price,
            yMax: pricingAlert.price,
            borderColor: '#900800',
            borderWidth: 1,
            borderDash: [1, 1],
          }

          // Label
          labelContent = (pricingAlert.trigger == 'low' ? 'Low' : 'High') + ' Trigger: $' + pricingAlert.price

          pricingAlertAnnotations[ pricingAlert.trigger + 'Label' + i ] = {
            type: 'label',
            position: 'start',
            xValue: 0,
            xAdjust: 5,
            yValue: pricingAlert.price,
            yAdjust: -12,
            padding: 4,
            borderWidth: 1,
            borderColor: '#900800',
            borderRadius: 3,
            backgroundColor: '#ffffff',
            color: '#900800',
            font: {
              size: 11,
            },
            content: [labelContent],
          }
        }

        // Existing Price
        // Only annotate for the "Supply" pricing component.
        if (
          currentDataRequest?.inputs?.existingSupplyPrice &&
          this.pricingComponent?.type == 'supply'
        ) {

          // Line
          pricingAlertAnnotations[ 'existingPriceLine' ] = {
            type: 'line',
            yMin: currentDataRequest.inputs.existingSupplyPrice,
            yMax: currentDataRequest.inputs.existingSupplyPrice,
            borderColor: '#294289',
            borderWidth: 1,
            borderDash: [1, 1],
          }

          // Label
          pricingAlertAnnotations[ 'existingPriceLabel' ] = {
            type: 'label',
            position: 'start',
            xValue: 0,
            xAdjust: 140,
            yValue: currentDataRequest.inputs.existingSupplyPrice,
            yAdjust: -12,
            padding: 4,
            borderWidth: 1,
            borderColor: '#294289',
            borderRadius: 3,
            backgroundColor: '#ffffff',
            color: '#294289',
            font: {
              size: 11,
            },
            content: ['Existing Price: $' + currentDataRequest.inputs.existingSupplyPrice],
          }
        }
      }

      return pricingAlertAnnotations
    },
  },
}
</script>
